import * as React from "react";
import { ComponentProps } from "react";
import { Slot } from "radix-ui";
import { cn } from "@repo/utils/cn";
import { tv, VariantProps } from "tailwind-variants";
import { twMergeConfig } from "@repo/utils/twMergeConfig";

export const text = tv(
  {
    base: "ui:font-body ui:transition-colors",
    variants: {
      variant: {
        micro: "ui:text-micro ui:tracking-[-0.12px] ui:xl:tracking-[-0.14px]",
        caption:
          "ui:text-caption ui:tracking-[-0.14px] ui:xl:tracking-[-0.16px]",
        body: "ui:text-body ui:tracking-[-0.16px] ui:xl:tracking-[-0.18px]",
        bodyLarge:
          "ui:text-bodyLarge ui:tracking-[-0.24px] ui:xl:tracking-[-0.32px]",
        navButton: "ui:text-navButton",
      },
      variantMd: {
        micro:
          "ui:md:text-micro ui:md:tracking-[-0.12px] ui:xl:tracking-[-0.14px]",
        caption:
          "ui:md:text-caption ui:md:tracking-[-0.14px] ui:xl:tracking-[-0.16px]",
        body: "ui:md:text-body ui:md:tracking-[-0.16px] ui:xl:tracking-[-0.18px]",
        bodyLarge:
          "ui:md:text-bodyLarge ui:md:tracking-[-0.24px] ui:xl:tracking-[-0.32px]",
        navButton: "ui:md:text-navButton",
      },
      variantXl: {
        micro: "ui:xl:text-micro ui:xl:tracking-[-0.14px]",
        caption: "ui:xl:text-caption ui:xl:tracking-[-0.16px]",
        body: "ui:xl:text-body ui:xl:tracking-[-0.18px]",
        bodyLarge: "ui:xl:text-bodyLarge ui:xl:tracking-[-0.32px]",
        navButton: "ui:xl:text-navButton",
      },
      weight: {
        normal: "ui:font-medium",
        strong: "ui:font-extrabold",
      },
      color: {
        primary: "ui:text-textPrimary",
        secondary: "ui:text-textSecondary",
        light: "ui:text-textLight",
        accent: "ui:text-textAmplified",
        inverted: "ui:text-textInverted",
      },
      mdColor: {
        primary: "ui:md:text-textPrimary",
        secondary: "ui:md:text-textSecondary",
        light: "ui:md:text-textLight",
        accent: "ui:md:text-textAmplified",
        inverted: "ui:md:text-textInverted",
      },
      xlColor: {
        primary: "ui:xl:text-textPrimary",
        secondary: "ui:xl:text-textSecondary",
        light: "ui:xl:text-textLight",
        accent: "ui:xl:text-textAmplified",
        inverted: "ui:xl:text-textInverted",
      },
    },
    defaultVariants: {
      variant: "body",
      weight: "normal",
      color: "primary",
    },
  },
  {
    twMergeConfig,
  },
);

export type TextProps = VariantProps<typeof text> & {
  as?: "div" | "p" | "h1" | "h2" | "h3";
  asChild?: boolean;
  opacity?: number;
  className?: string;
  fontFeatures?: string[];
} & (
    | {
        asChild: true;
        children?: React.ReactNode;
      }
    | ({
        asChild?: false;
      } & ComponentProps<"p">)
  );

export function Text({
  as,
  asChild,
  opacity,
  className,
  variant,
  variantMd,
  variantXl,
  weight,
  color,
  mdColor,
  xlColor,
  ...props
}: TextProps) {
  const Component = asChild ? Slot.Root : as || "p";

  delete props.fontFeatures;

  return (
    <Component
      {...props}
      className={cn(
        text({
          variant,
          variantMd,
          variantXl,
          weight,
          color,
          mdColor: mdColor || color,
          xlColor: xlColor || mdColor || color,
        }),
        className,
      )}
      style={
        {
          opacity: opacity ? opacity / 100 : undefined,
          ...("style" in props ? props.style : {}),
        } as React.CSSProperties
      }
    />
  );
}
